import { useCallback, useMemo } from 'react'
import { useToast } from '../../context/useToast'
import { useChat } from '../../context/useChat'
import { useOrbit } from '../Common/Orbit/useOrbit'
import { withLcmChip } from '../Common/Orbit/lcmOrbitSlices'
import { useLcmChipDispatchers } from '../Common/Orbit/useLcmChipDispatchers'
import type { OrbitActionBridge } from '../Common/Orbit/orbitSliceHelpers'
import { buildChannelRowSlices, buildMentionChipSlices, buildMessageBubbleSlices } from './chat.orbit'
import { ORBIT_SLICE_TO_ACTION } from '../Common/Orbit/orbitSliceIdMap'
import { invokeAction } from '../../services/actions/registry'
import { useAuth } from '../../context/useAuth'
import { useWorkspace } from '../../context/useWorkspace'
import { RailPageShell, ControlNotice } from '../Common'
import { useChatControls } from '../../hooks/useChatControls'
import { useNls } from '../../nls/nlsProvider'
import { getChatShellCopy } from './chatShellChrome'
import { ChannelHeader } from './ChannelHeader'
import { TeamGroupChat, type TeamGroupChatProps } from './TeamGroupChat'
import { ChatChannelRail } from './ChatChannelRail'
import { ChannelMembersCard } from './ChannelMembersCard'
import { ChatAiMagicPanel } from './ChatAiMagicPanel'
import { ChatSelectionProvider } from '../../context/ChatSelectionContext'
import type { AiChatAttachment, AiChatSession } from '../../services/aiChatHistoryService'
import type { AiChatHandoffTarget } from '../../types/aiChatSource'
import './chatPage3col.css'

type ChatPageProps = {
  onSendChatsToAi?: (payload: { attachments: AiChatAttachment[]; target: AiChatHandoffTarget }) => void
  onOpenAiMagic?: () => void
  onOpenAiMagicSession?: (session: AiChatSession) => void
  onElementMentionClick?: (elementId: string) => void
  helperSyncState?: string
  helperQuickActions?: TeamGroupChatProps['helperQuickActions']
  renderHelperSummary?: TeamGroupChatProps['renderHelperSummary']
  onTranslateMessage?: TeamGroupChatProps['onTranslateMessage']
  /** Retained for source-compat with prior helper/member call-sites. The 3C
   * layout is desktop-only; helper/member surfaces continue to render
   * `TeamGroupChat` directly and never mount this page. */
  platform?: TeamGroupChatProps['platform']
}

/**
 * ChatPage — 3-column layout for the workspace chat hub.
 *
 * Left rail:   ChatChannelRail (Channels / Tags switcher, AI-Magic-style pills)
 * Middle:      TeamGroupChat in chromeless mode (message list + composer only —
 *              no top bar, no user footer)
 * Right sidecar: ChannelMembersCard + ChatAiHistoryCard stacked 50/50
 */
export const ChatPage = ({
  onSendChatsToAi,
  onOpenAiMagic,
  onOpenAiMagicSession,
  onElementMentionClick,
  helperSyncState,
  helperQuickActions,
  renderHelperSummary,
  onTranslateMessage,
}: ChatPageProps) => {
  const { showToast } = useToast()
  const { deleteMessage, renameChannel, leaveChannel, requestCreateChannelDraft } = useChat()
  const { currentUser } = useAuth()
  const { activeWorkspaceId, canPerform } = useWorkspace()
  const { mode: chatMode, canCreateChannel, canCreateDm } = useChatControls()
  const lcmDispatchers = useLcmChipDispatchers()
  const onOrbitAction = useCallback<OrbitActionBridge>((sliceId, label, id, title, phase6) => {
    if (phase6) {
      showToast('Coming in Orbit Phase 6')
      return
    }
    const actionId = ORBIT_SLICE_TO_ACTION[sliceId]
    if (actionId) {
      void invokeAction(actionId, { id, title }, {
        workspaceId: activeWorkspaceId ?? '',
        actor: { uid: currentUser?.uid ?? '' },
        canPerform,
        showToast,
        invokedFrom: 'orbit',
        chatOps: {
          deleteMessage: async (msgId: string) => {
            await deleteMessage(msgId)
          },
          renameChannel: async (chanId: string, newName: string) => {
            await renameChannel(chanId, newName)
          },
          leaveChannel: async (chanId: string) => {
            await leaveChannel(chanId)
          },
          replyToMessage: async () => {
            showToast('Reply ready', 'info')
          },
          convertToTask: async () => {
            showToast('Convert to task ready', 'info')
          },
          openDm: async () => {
            if (!canCreateDm) return
            showToast('Direct message ready', 'info')
          },
          configureNotifications: async () => {
            showToast('Notifications configured', 'info')
          },
        },
      })
      return
    }
    const lcmDispatch = lcmDispatchers[sliceId]
    if (lcmDispatch) {
      lcmDispatch(id, title)
      return
    }
    console.info('[orbit] unwired', sliceId)
    showToast(`${label}${title ? ` · ${title}` : ''}`)
    // TODO(orbit-handler-missing): sliceIds that hit this fallback still need real primitives — grep console for "[orbit] unwired" to enumerate.
  }, [activeWorkspaceId, canCreateDm, canPerform, currentUser?.uid, deleteMessage, lcmDispatchers, leaveChannel, renameChannel, showToast])
  const orbitDescriptor = useMemo(() => ({
    page: 'chat',
    centerLabel: 'Chat',
    onCreate: () => {
      if (!canCreateChannel) return
      requestCreateChannelDraft({ name: '', type: 'public' })
    },
    contextSlices: () => [],
    targets: withLcmChip({
      'message-bubble': (node: HTMLElement) => buildMessageBubbleSlices(node, onOrbitAction),
      'channel-row': (node: HTMLElement) => buildChannelRowSlices(node, onOrbitAction),
      'mention-chip': (node: HTMLElement) => buildMentionChipSlices(node, onOrbitAction, canCreateDm),
    }, onOrbitAction),
  }), [canCreateChannel, canCreateDm, onOrbitAction, requestCreateChannelDraft])
  useOrbit(orbitDescriptor)
  const { t } = useNls()
  const chatCopy = getChatShellCopy(t)

  if (chatMode === 'OFF') {
    return (
      <div className="chatPageControlNoticeOnly" style={{ padding: '24px', maxWidth: '640px', margin: '0 auto' }}>
        <ControlNotice
          tone="warning"
          message={t('NLSControlNoticeChatOff')}
          testId="chat-control-notice-off"
        />
      </div>
    )
  }

  return (
    <ChatSelectionProvider>
    <RailPageShell
      variant="three-column"
      ariaLabel={chatCopy.pageLabel}
      className="chatPage3C"
      identity={{ badge: 'C', title: chatCopy.pageLabel, subtitle: t('NLSChatLabelChatsDiscussions') }}
      useCollapsedInitials
      leftRail={<ChatChannelRail />}
      middleStage={(
        <div className="chatPage3C__stageHost">
          <ChannelHeader />
          <TeamGroupChat
            platform="desktop"
            chromeless
            onElementMentionClick={onElementMentionClick}
            helperSyncState={helperSyncState}
            helperQuickActions={helperQuickActions}
            renderHelperSummary={renderHelperSummary}
            onTranslateMessage={onTranslateMessage}
          />
        </div>
      )}
      rightSidecar={(
        <div className="chatPage3C__sidecarScroll">
          <ChannelMembersCard />
          <ChatAiMagicPanel
            onOpenAiMagic={onOpenAiMagic}
            onOpenAiMagicSession={onOpenAiMagicSession}
            onSendChatsToAi={onSendChatsToAi}
          />
        </div>
      )}
    />
    </ChatSelectionProvider>
  )
}
